import { z } from 'zod';

/**
 * Installs a Zod error map on the zod instance that **this package** was built
 * against.
 *
 * `@autopia/api-contracts` ships as CommonJS, so every schema it exports is
 * constructed with zod's CJS module instance (`zod/index.cjs`). An app that
 * imports zod as ESM (`import { z } from 'zod'` → `zod/index.js`) gets a
 * *separate* module instance with its own error map. Because Zod resolves error
 * messages against the error map of the instance that built the schema, calling
 * `z.setErrorMap()` in the app localizes only the app's own schemas — validation
 * errors thrown by api-contracts schemas (e.g. those fed straight into
 * `zodResolver(SomeRequestSchema)`) keep zod's default English messages.
 *
 * Apps should call this **in addition to** their own `z.setErrorMap()` so both
 * zod instances share the same locale-aware error map:
 *
 * ```ts
 * import { z } from 'zod';
 * import { setZodErrorMap } from '@autopia/api-contracts';
 *
 * const errorMap = makeZodErrorMap(locale);
 * z.setErrorMap(errorMap);   // app's ESM zod instance
 * setZodErrorMap(errorMap);  // api-contracts' CJS zod instance
 * ```
 *
 * The error map itself is instance-agnostic — it only reads issue codes (plain
 * string constants) and returns plain `{ message }` objects — so the same map
 * value can be shared safely between both instances.
 */
export function setZodErrorMap(errorMap: z.ZodErrorMap): void {
  z.setErrorMap(errorMap);
}
